import { Session, StockSymbol } from '@benzinga/session';
import { QuoteFeedEvent, QuotesManager } from '@benzinga/quotes-manager';
import { Subscription, Subscribable, SubscribableMultiplexer } from '@benzinga/subscribable';

import { Holding, WatchlistManager } from '@benzinga/watchlist-manager';

import { HoldingsQuote } from './entities';
import { debounce } from '@benzinga/utils';

interface QuoteUpdateEvent {
  quote: HoldingsQuote;
  type: 'quote-holdings:quote-update';
}

interface QuotesUpdateEvent {
  quotes: HoldingsQuote[];
  type: 'quote-holdings:update';
}

export type QuoteHoldingsFeedEvent = QuoteUpdateEvent | QuotesUpdateEvent;
export class QuoteHoldingsFeed extends Subscribable<QuoteHoldingsFeedEvent> {
  private symbols: StockSymbol[];
  private holding = new Map<StockSymbol, Holding>();
  private watchlistManager: WatchlistManager;
  private watchlistSubscription: Subscription<WatchlistManager> | undefined;

  private quotesManager: QuotesManager;
  private quotesManagerSubscription: Subscription<QuotesManager> | undefined;
  private quotesMultiplexer: SubscribableMultiplexer<QuoteFeedEvent>;
  private quotesMultiplexerSubscription: Subscription<SubscribableMultiplexer<QuoteFeedEvent>> | undefined;

  private setIntervalHandle: ReturnType<typeof setInterval> | undefined;
  private quoteHoldings = new Map<StockSymbol, HoldingsQuote>([]);
  private quoteHoldingList?: HoldingsQuote[];

  private subscribeToQuotes: boolean;

  constructor(symbols: StockSymbol[], subscribeToQuotes: boolean, session: Session) {
    super();
    this.symbols = symbols;
    this.subscribeToQuotes = subscribeToQuotes;

    this.watchlistManager = session.getManager(WatchlistManager);
    this.quotesManager = session.getManager(QuotesManager);

    this.getHoldings();
    this.quotesMultiplexer = new SubscribableMultiplexer<QuoteFeedEvent>(
      subscribeToQuotes ? symbols.map(symbol => [symbol, this.quotesManager.createQuoteFeed(symbol)]) : [],
    );
    symbols.forEach(s => this.getQuoteHolding(s));
  }

  public getCachedValue = (): HoldingsQuote[] => {
    if (this.quoteHoldingList === undefined) {
      this.quoteHoldingList = Array.from(this.quoteHoldings.values());
    }
    return this.quoteHoldingList;
  };

  public getSymbols = () => this.symbols;

  public addSymbols = (symbols: StockSymbol[]) => {
    this.symbols = [...this.symbols, ...symbols];
    symbols.forEach(s => {
      this.quotesMultiplexer.add(s, this.quotesManager.createQuoteFeed(s));
    });
    this.dispatch({ quotes: symbols.map(s => this.getQuoteHolding(s)), type: 'quote-holdings:update' });
    this.getHoldings();
  };

  protected onFirstSubscription() {
    this.quotesManagerSubscription = this.quotesManager.subscribe(event => {
      switch (event.type) {
        case 'quote:detailed_quotes': {
          this.quoteHoldings.clear();
          this.quoteHoldingList = undefined;

          if (this.subscribeToQuotes) {
            this.quotesMultiplexer.replace(this.symbols.map(s => [s, this.quotesManager.createQuoteFeed(s)]));
          }
          this.dispatch({ quotes: this.symbols.map(s => this.getQuoteHolding(s)), type: 'quote-holdings:update' });
        }
      }
    });

    this.updateValues();
    const debounceUpdateValue = debounce(this.updateValues, 30000) as () => void;
    this.setIntervalHandle = setInterval(debounceUpdateValue, 60000);

    this.quotesMultiplexerSubscription = this.quotesMultiplexer.subscribe(event => {
      switch (event.type) {
        case 'quote:quote': {
          this.dispatch({
            quote: this.getQuoteHolding(event.quote.symbol),
            type: 'quote-holdings:quote-update',
          });
        }
      }
    });

    this.watchlistSubscription = this.watchlistManager.subscribe(event => {
      switch (event.type) {
        case 'watchlist:updated_watchlists':
          this.quoteHoldings.clear();
          this.quoteHoldingList = undefined;

          this.getHoldings();

          this.dispatch({
            quotes: this.symbols.map(s => this.getQuoteHolding(s)),
            type: 'quote-holdings:update',
          });
      }
    });
  }

  protected onZeroSubscriptions() {
    if (this.setIntervalHandle) {
      clearInterval(this.setIntervalHandle);
    }
    this.quotesMultiplexerSubscription?.unsubscribe();
    this.quotesManagerSubscription?.unsubscribe();
    this.watchlistSubscription?.unsubscribe();
  }

  private getHoldings = () => {
    this.holding.clear();

    this.watchlistManager
      .getStoredWatchlists()
      ?.flatMap(w =>
        w.symbols.filter(s => s.holding !== undefined).map(s => ({ symbol: s.symbol, ...(s.holding as Holding) })),
      )
      ?.forEach(h => {
        const holding = this.holding.get(h.symbol);
        if (holding) {
          this.holding.set(h.symbol, {
            ...holding,
            averageBuyingPrice: (holding.costBasis + h.costBasis) / (holding.quantity + h.quantity),
            costBasis: holding.costBasis + h.costBasis,
            price: (holding.costBasis + h.costBasis) / (holding.quantity + h.quantity),
            quantity: holding.quantity + h.quantity,
          });
        } else {
          this.holding.set(h.symbol, h);
        }
      });
  };

  private updateValues = () => {
    this.watchlistManager.getWatchlists(false);

    // TODO this is a temporary fix. since some watchlist have a lot of symbols.
    // we need to change getDetailedQuotes to use post instead of get requests
    this.symbols
      .reduce<string[][]>((acc, symbol, index) => {
        if (index % 50 === 0) {
          acc.push([]);
        }
        acc[acc.length - 1].push(symbol);
        return acc;
      }, [])
      .map(symbols => this.quotesManager.getDetailedQuotes(symbols));
  };

  private getQuoteHolding = (symbol: StockSymbol): HoldingsQuote => {
    const holding = this.holding.get(symbol);
    const quote = this.quotesManager.getCachedQuote(symbol);

    const lastPrice = quote?.currentPrice ?? 0;

    const acquiredPrice = holding?.price ?? 0;
    const acquiredQuantity = holding?.quantity ?? 0;

    const originalValue = acquiredPrice * acquiredQuantity;
    const currentValue = lastPrice * acquiredQuantity;

    const dollarChange = currentValue - originalValue;
    const isIncrease = dollarChange > 0;
    let percentChange = 0;
    if (isIncrease) {
      const increase = currentValue - originalValue;
      percentChange = (increase / originalValue) * 100;
    } else {
      const decrease = originalValue - currentValue;
      percentChange = -(decrease / originalValue) * 100;
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      askPrice: _askPrice,
      bidPrice: _bidPrice,
      change: _change,
      currency: _currency,
      fiftyTwoWeekHigh: _fiftyTwoWeekHigh,
      fiftyTwoWeekLow: _fiftyTwoWeekLow,
      lastTradePrice: _lastTradePrice,
      lastTradeTime: _lastTradeTime,
      open: _open,
      volume: _volume,
      ...detailedQuote
    } = this.quotesManager.getDetailedQuotesCached([symbol])[symbol] ?? {};
    /* eslint-enable @typescript-eslint/no-unused-vars */

    const holdingQuotes = {
      ...detailedQuote,
      ...quote,
      cost: originalValue,
      gainDollar: lastPrice ? dollarChange : null,
      gainPercentage: lastPrice ? percentChange : null,
      marketValue: lastPrice ? currentValue : null,
      quantity: acquiredQuantity,
      symbol,
    };

    this.quoteHoldings.set(symbol, holdingQuotes);
    this.quoteHoldingList = undefined;

    return holdingQuotes;
  };
}
